import { useMemo } from 'react';
import { useTheme } from '@material-ui/core';
import type { Place, RowBoundary } from '../types/warehouse.types';
import { Row2DProjectionRenderer } from '../renderers/Row2DProjectionRenderer';
import { getRowId } from '../utils/placeUtils';

interface Row2DProjectionDimensions {
  canvasHeight: number;
  columnCount: number;
  palletSize: number;
  activeLevelCount: number;
  minPlaceNumber: number;
  maxPlaceNumber: number;
}

export const useRow2DProjectionDimensions = (
  places: Map<string, Place>,
  selectedRow: RowBoundary,
  activeLevels: Set<number>,
  canvasWidth: number
): Row2DProjectionDimensions => {
  const theme = useTheme();

  return useMemo(() => {
    // Get default config from renderer
    const renderer = new Row2DProjectionRenderer(theme);
    const config = renderer.getConfig();
    const {
      titleHeight,
      topMargin,
      palletSpacing,
      levelSpacing,
      maxPalletSize,
      labelMargin,
      bottomLabelHeight
    } = config;

    // Filter places for this row (same logic as in renderer)
    const rowPlaces = Array.from(places.values()).filter(p => {
      if (!activeLevels.has(p.level)) return false;
      const rowId = getRowId(p);
      return selectedRow.name.includes(rowId) || selectedRow.name === rowId;
    });

    // Default values if no places
    if (rowPlaces.length === 0) {
      return {
        canvasHeight: 100,
        columnCount: 0,
        palletSize: 0,
        activeLevelCount: 0,
        minPlaceNumber: 0,
        maxPlaceNumber: 0
      };
    }

    // Find min and max place numbers to show all columns (including deleted places)
    const { min: minPlaceNumber, max: maxPlaceNumber, count: columnCount } =
      Row2DProjectionRenderer.getRowPlaceNumberRange(rowPlaces);

    // Get levels that actually exist in this row (not all active levels from filter)
    const rowLevels = Row2DProjectionRenderer.getRowLevels(rowPlaces);
    const activeLevelCount = rowLevels.size;

    // Calculate dimensions
    const totalSpacing = (columnCount - 1) * palletSpacing;
    const palletSize = Math.min(maxPalletSize, (canvasWidth - labelMargin - 40 - totalSpacing) / columnCount);
    const totalLevelSpacing = (activeLevelCount - 1) * levelSpacing;
    const totalHeight = activeLevelCount * palletSize + totalLevelSpacing;
    const canvasHeight = titleHeight + totalHeight + topMargin * 2 + bottomLabelHeight;

    return {
      canvasHeight,
      columnCount,
      palletSize,
      activeLevelCount,
      minPlaceNumber,
      maxPlaceNumber
    };
  }, [places, selectedRow, activeLevels, canvasWidth, theme]);
};
